/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-shadow */
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import { Typography } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Tabs from '@mui/material/Tabs'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AppContext from '../../context/appContext'
import { useDataCollectionStatement } from '../../hooks/data/useDataCollectionStatement'
import { useFormDefinitions } from '../../hooks/data/useFormDefinitions'
import { getDirtyForm, setDirtyForm } from '../../services/appPreferences/dirtyFormStorage'
import { formModes, metricEventStatus } from '../../utils/constants'
import { DCNamespaces } from '../../utils/dataCollection/namespaces'
import {
  mergeNamespaceStatus,
  mergeValidatorStatuses,
} from '../../utils/dataCollection/namespacesValidations/mergeNamespaceStatus'
import { namespacesValidations } from '../../utils/dataCollection/namespacesValidations/index'
import FormContainer from '../FormContainer/FormContainer'
import DirtyForm from '../formPanel/modals/DirtyForm'
import { NavHeader } from '../NavHeader/NavHeader'
import HolisticPlanIssuesWarning from './HolisticPlanIssuesWarning'
import Tab from './Tab'

const runDisabledValidations = async (dependencies, farm, year, namespace) => {
  if (!dependencies?.disabled?.length) return true

  const validationPromises = dependencies.disabled
    .map(disabledDependency => namespacesValidations[disabledDependency])
    .filter(validation => validation !== undefined)
    .map(validation => validation(farm, year, namespace))

  const statuses = (await Promise.all(validationPromises)).filter(result => result !== true)
  return statuses.length ? mergeValidatorStatuses(statuses) : true
}

const DataCollectionForm = () => {
  const [formKey, setFormKey] = useState(null)

  const [open, setOpen] = useState(false)
  const [openLeave, setOpenLeave] = useState(false)
  const [temporalKey, setTemporalKey] = useState(null)

  const navigate = useNavigate()
  const { year } = useParams()
  const { setHideNavbar, currentFarm, programConfig } = useContext(AppContext)

  const [tabs, setTabs] = useState([])
  const latestRunRef = useRef(0)
  const [formMode, setFormMode] = useState(null)
  const [selectedMetric, setSelectedMetric] = useState(null)

  const { data: dataCollectionStatement } = useDataCollectionStatement(year)
  const { data: formDefinitions } = useFormDefinitions(programConfig)

  const changeTab = newValue => {
    if (open) {
      setOpen(false)
      setTemporalKey(null)
      setFormKey(temporalKey)
      setDirtyForm(false)
    } else {
      setFormKey(newValue)
      setDirtyForm(false)
    }
  }

  const handleChange = (event, newValue) => {
    if (getDirtyForm() && !open) {
      setTemporalKey(newValue)
      setOpen(true)
    } else {
      changeTab(newValue)
    }
  }

  const handleCancelChange = () => {
    setOpen(false)
    setTemporalKey(null)
  }

  const checkNamespaceDependencies = useCallback(
    async (namespace, dependencies, farm, _year) => {
      const [dependencyStatus, findingsStatus] = await Promise.all([
        runDisabledValidations(dependencies, farm, _year, namespace),
        namespacesValidations.unresolvedFindings(dataCollectionStatement?.id, namespace),
      ])

      return mergeNamespaceStatus(dependencyStatus, findingsStatus)
    },
    [dataCollectionStatement?.id],
  )

  const handleResetSelectedTab = async () => {
    const selectedTabIndex = tabs.findIndex(item => item.namespace === formKey)
    if (selectedTabIndex !== -1) {
      const newTabs = [...tabs]

      const selectedTab = newTabs[selectedTabIndex]
      newTabs[selectedTabIndex] = {
        ...selectedTab,
        status: await checkNamespaceDependencies(
          selectedTab.namespace,
          selectedTab.dependencies,
          currentFarm,
          year,
        ),
      }

      // When a namespace is changed, all the namespaces that depend on it need to be reloaded
      // This is done by checking the dependencies.reloadNamespace of the selected tab
      if (selectedTab?.dependencies?.reloadNamespace?.length) {
        for (const namespaceToReload of selectedTab?.dependencies?.reloadNamespace) {
          const tabIndexToReload = tabs.findIndex(item => item.namespace === namespaceToReload)
          if (tabIndexToReload !== -1) {
            const tabToReload = newTabs[tabIndexToReload]
            newTabs[tabIndexToReload] = {
              ...tabToReload,
              status: await checkNamespaceDependencies(
                namespaceToReload,
                tabToReload.dependencies,
                currentFarm,
                year,
              ),
            }
          }
        }
      }

      setTabs(newTabs)
    }
  }

  useEffect(() => {
    setHideNavbar(true)
    return () => {
      setHideNavbar(false)
    }
  }, [setHideNavbar])

  const handleNavBack = () => {
    if (getDirtyForm() && !openLeave) {
      setOpenLeave(true)
    } else {
      setOpenLeave(false)
      navigate('/management')
    }
  }

  useEffect(() => {
    if (!dataCollectionStatement) return
    const selectedTab = tabs.find(item => item.namespace === formKey)
    if (!selectedTab) return

    function getFormMode({ tab, actionsAllowed }) {
      if (tab?.status?.disabled) return formModes.Disable
      if (tab?.status?.readOnly) return formModes.View

      if (selectedMetric && selectedMetric.isDraft) return formModes.Edit
      if (selectedMetric && selectedMetric.metricStatusId !== metricEventStatus.Open)
        return formModes.View

      if (actionsAllowed.edit) return formModes.Edit
      if (actionsAllowed.review) return formModes.Review
      if (actionsAllowed.view) return formModes.View

      return { label: 'Sin permisos', readOnly: true }
    }

    setFormMode(
      getFormMode({ tab: selectedTab, actionsAllowed: dataCollectionStatement.actionsAllowed }),
    )
  }, [currentFarm, year, dataCollectionStatement, tabs, formKey, selectedMetric])

  useEffect(() => {
    latestRunRef.current += 1
    const run = latestRunRef.current

    async function computeTabs() {
      const mapeoLabelsDefinitions = await Promise.all(
        formDefinitions.map(async formDefinition => ({
          label: formDefinition?.label,
          dependencies: formDefinition?.dependencies,
          status: await checkNamespaceDependencies(
            formDefinition.namespace,
            formDefinition.dependencies,
            currentFarm,
            year,
          ),
          namespace: formDefinition?.namespace,
        })),
      )
      // A year or definitions change starts a new run while this one still awaits its checks, so
      // only the newest run may commit its statuses.
      if (run !== latestRunRef.current) return

      const firstEnabledTab = mapeoLabelsDefinitions.find(tab => !tab.status.disabled)
      setFormKey(
        prevFormKey => prevFormKey || firstEnabledTab?.namespace || formDefinitions[0]?.namespace,
      )
      setTabs(mapeoLabelsDefinitions)
    }

    if (currentFarm && year && dataCollectionStatement && formDefinitions?.length > 0) {
      computeTabs()
    }
  }, [currentFarm, checkNamespaceDependencies, year, dataCollectionStatement, formDefinitions])

  return (
    formMode &&
    formKey &&
    year &&
    currentFarm &&
    dataCollectionStatement && (
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100vw', height: '100%' }}>
        <NavHeader>
          <NavHeader.BackButton handleNavBack={handleNavBack}>
            <ArrowBackIosIcon />
          </NavHeader.BackButton>
          <NavHeader.Title>
            <Typography variant="bodyBold">CARGA DE DATOS ANUALES</Typography>
            <Typography
              sx={{ ml: 1, textTransform: 'lowercase' }}
            >{`(${formMode.label})`}</Typography>
          </NavHeader.Title>
          <NavHeader.FarmDetails />
        </NavHeader>

        <Stack direction="row" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs aria-label="management-activities" gap={2} value={formKey} onChange={handleChange}>
            {tabs.map(tab => (
              <Tab
                key={tab.namespace}
                disabled={tab?.status?.disabled}
                label={tab.label}
                tooltipError={tab?.status?.error}
                totalError={tab?.status?.totalError}
                typeError={tab?.status?.disabled ? 'error' : 'warning'}
                value={tab.namespace}
              />
            ))}
          </Tabs>
        </Stack>

        {/* Plans from closed monitoring periods stay as they were loaded. */}
        {formKey === DCNamespaces.grazing &&
          !dataCollectionStatement.isInBlockedMonitoringPeriod && (
            <HolisticPlanIssuesWarning farm={currentFarm} year={year} />
          )}

        <FormContainer
          dataCollectionStatementId={dataCollectionStatement.id}
          formKey={formKey}
          formMode={formMode}
          resetTab={handleResetSelectedTab}
          selectedMetricEvent={selectedMetric}
          setSelectedMetricEvent={setSelectedMetric}
          year={year}
        />

        {/* Dialog para caso que sigue en el mismo año */}
        <DirtyForm handleClose={handleCancelChange} handleConfirm={handleChange} open={open} />

        {/* Dialog para caso que vuelve a la tabla */}
        <DirtyForm
          handleClose={() => {
            setOpenLeave(false)
          }}
          handleConfirm={() => {
            setOpenLeave(false)
            navigate('/management')
          }}
          open={openLeave}
        />
      </Box>
    )
  )
}

export default DataCollectionForm
